import styles from "./transfers-lookup.module.scss"

import { useContext, useEffect, useMemo, useState } from "react"
import moment from "moment"
import firebase from "firebase"

import { BookingContext } from "../../../utils/providers/booking"
import { AlertsContext } from "../../../utils/providers/alerts"
import { auth, db } from "../../../utils/firebase"
import { searchTransferQuotes } from "./_utils/transfers-lookup"
import golfRoutes from "./_utils/golf-routes.json"

import Input from "../../../components/ui/inputs/input"
import Button from "../../../components/ui/button/button"
import Checkbox from "../../../components/ui/checkbox/checkbox"
import Select from "../../../components/ui/select/select"
import { SearchIcon } from "../../../utils/svgs"
import { ArrowRightIcon } from "../../../utils/svgs-v2"

const JOURNEY_MODES = [
	{ id: "pickup_time", label: "Standard", hint: "Point-to-point — choose the time you need to be collected." },
	{ id: "flight_ship_train_time", label: "Flight", hint: "The transfer is timed against an arrival — use the scheduled arrival time." },
	{ id: "golf_tee_time", label: "Golf tee time", hint: "Used for resort transfers timed against a booked tee slot." },
]

const deriveJourneyMode = (search = {}) => {
	if (search.flight_ship_train_time) return "flight_ship_train_time"
	if (search.golf_tee_time) return "golf_tee_time"
	return "pickup_time"
}

const ROUTE_SOURCES = golfRoutes.sources || {}
const ROUTE_DESTINATIONS = golfRoutes.destinations || {}
const ROUTE_PAIRS = golfRoutes.routes || {}

const buildDestinationsForSource = (sourceId) => {
	if (!sourceId || !ROUTE_PAIRS[sourceId]) {
		return ROUTE_DESTINATIONS
	}

	return ROUTE_PAIRS[sourceId].reduce((acc, destId) => {
		if (ROUTE_DESTINATIONS[destId]) {
			acc[destId] = ROUTE_DESTINATIONS[destId]
		}
		return acc
	}, {})
}

const buildInitialSearch = ({ booking, golfers, nonGolfers }) => {
	const savedSearch = booking?.transfers_lookup_search || {}
	const travelDate = savedSearch.travel_date || (booking?.check_in?.seconds ? moment.unix(booking.check_in.seconds).utc().format("YYYY-MM-DD") : "")
	const fallbackAdults = Number(savedSearch.adults || golfers || 0) + Number(nonGolfers || 0)

	return {
		from_destination: savedSearch.from_destination ? `${savedSearch.from_destination}` : "",
		to_destination: savedSearch.to_destination ? `${savedSearch.to_destination}` : "",
		travel_date: travelDate,
		pickup_time: savedSearch.pickup_time || "",
		flight_ship_train_time: savedSearch.flight_ship_train_time || "",
		golf_tee_time: savedSearch.golf_tee_time || "",
		adults: savedSearch.adults || (fallbackAdults > 0 ? `${fallbackAdults}` : ""),
		hold_luggage: Boolean(savedSearch.hold_luggage),
	}
}

const buildPayload = (search) =>
	Object.entries(search).reduce((acc, [key, value]) => {
		if (typeof value === "boolean") {
			acc[key] = value
			return acc
		}

		if (value === null || value === undefined) {
			return acc
		}

		const cleanedValue = `${value}`.trim()
		if (!cleanedValue) {
			return acc
		}

		if (key === "adults") {
			acc[key] = Number(cleanedValue)
			return acc
		}

		acc[key] = cleanedValue
		return acc
	}, {})

const formatSavedDate = (savedAt) => {
	if (!savedAt?.seconds) {
		return ""
	}

	return moment.unix(savedAt.seconds).format("DD/MM/YYYY [at] HH:mm")
}

export default function TransfersLookup() {
	const { bookingID, booking, golfers, nonGolfers } = useContext(BookingContext)
	const { pushAlert } = useContext(AlertsContext)

	const [search, setSearch] = useState(() => buildInitialSearch({ booking: {}, golfers: 0, nonGolfers: 0 }))
	const [journeyMode, setJourneyMode] = useState("pickup_time")
	const [loading, setLoading] = useState(false)
	const [savingSelection, setSavingSelection] = useState(false)
	const [searchError, setSearchError] = useState("")
	const [normalizedSearch, setNormalizedSearch] = useState({})
	const [basketId, setBasketId] = useState(null)
	const [quotes, setQuotes] = useState([])
	const [selectedQuoteId, setSelectedQuoteId] = useState(null)
	const [savedSelection, setSavedSelection] = useState(null)

	useEffect(() => {
		const nextSearch = buildInitialSearch({ booking, golfers, nonGolfers })
		setSearch(nextSearch)
		setJourneyMode(deriveJourneyMode(nextSearch))
		setSavedSelection(booking?.transfers_lookup_selection || null)
	}, [booking, golfers, nonGolfers])

	const selectedQuote = useMemo(() => quotes.find((quote) => quote.id === selectedQuoteId) || null, [quotes, selectedQuoteId])

	const destinationOptions = useMemo(() => buildDestinationsForSource(search.from_destination), [search.from_destination])

	const updateField = (key, value) => {
		setSearch((currentSearch) => ({
			...currentSearch,
			[key]: value,
		}))
	}

	const changeJourneyMode = (modeId) => {
		setJourneyMode(modeId)
		setSearch((currentSearch) => ({
			...currentSearch,
			pickup_time: modeId === "pickup_time" ? currentSearch.pickup_time : "",
			flight_ship_train_time: modeId === "flight_ship_train_time" ? currentSearch.flight_ship_train_time : "",
			golf_tee_time: modeId === "golf_tee_time" ? currentSearch.golf_tee_time : "",
		}))
	}

	const activeJourney = JOURNEY_MODES.find((mode) => mode.id === journeyMode) || JOURNEY_MODES[0]

	const validateSearch = () => {
		const hasOrigin = search.from_destination
		const hasDestination = search.to_destination
		const hasJourneyTime = search.pickup_time || search.flight_ship_train_time || search.golf_tee_time

		if (!hasOrigin || !hasDestination) {
			pushAlert({
				type: "ALERT",
				title: "Missing locations",
				body: "Choose both a pickup and a drop-off point before searching.",
			})
			return false
		}

		if (!search.travel_date) {
			pushAlert({
				type: "ALERT",
				title: "Missing travel date",
				body: "Choose the transfer travel date before searching.",
			})
			return false
		}

		if (!hasJourneyTime) {
			pushAlert({
				type: "ALERT",
				title: "Missing journey time",
				body: "Provide a pickup time, arrival time, or tee time before searching.",
			})
			return false
		}

		return true
	}

	const handleSearch = async () => {
		if (!validateSearch()) {
			return
		}

		setLoading(true)
		setSearchError("")
		setSelectedQuoteId(null)

		const response = await searchTransferQuotes({
			bookingID,
			search: buildPayload(search),
		})

		if (!response?.result) {
			const errorMessage = response?.detail || response?.error || "Unable to search transfer quotes right now."
			setQuotes([])
			setBasketId(null)
			setNormalizedSearch({})
			setSearchError(errorMessage)
			pushAlert({
				type: "ALERT",
				title: "Transfer search failed",
				body: response?.error || "The Lifts To search did not complete successfully.",
			})
			setLoading(false)
			return
		}

		setQuotes(response.quotes || [])
		setBasketId(response.basketId || null)
		setNormalizedSearch(response.search || {})
		setSelectedQuoteId(response.quotes?.[0]?.id || null)
		setLoading(false)
	}

	const persistSelection = async () => {
		if (!selectedQuote) {
			return
		}

		setSavingSelection(true)

		const selectionSummary = {
			basket_id: basketId || null,
			quote_id: selectedQuote.id,
			journey_status: selectedQuote.journeyStatus || null,
			transfer_type: selectedQuote.transferType || null,
			short_description: selectedQuote.shortDescription || "",
			vehicle_type: selectedQuote.vehicleType || null,
			supplier_name: selectedQuote.supplierName || null,
			price: selectedQuote.price,
			currency: selectedQuote.currency || null,
			pickup_label: selectedQuote.pickupLabel || null,
			dropoff_label: selectedQuote.dropoffLabel || null,
			pickup_time: selectedQuote.pickupTime || null,
			passenger_capacity: selectedQuote.passengerCapacity || null,
			hold_luggage: typeof selectedQuote.holdLuggage === "boolean" ? selectedQuote.holdLuggage : null,
			raw: selectedQuote.raw || {},
			saved_at: firebase.firestore.FieldValue.serverTimestamp(),
			saved_by: auth.currentUser?.uid || null,
		}

		await db.doc(`bookings/${bookingID}`).set(
			{
				transfers_lookup_search: normalizedSearch,
				transfers_lookup_selection: selectionSummary,
			},
			{ merge: true }
		)

		await db.collection(`bookings/${bookingID}/logs`).add({
			type: "TRANSFERS_LOOKUP_SELECTED",
			message: `A Transfers Lookup option was saved${selectedQuote.shortDescription ? `: ${selectedQuote.shortDescription}` : "."}`,
			created: firebase.firestore.FieldValue.serverTimestamp(),
			badge: "INFO",
			user: auth.currentUser?.uid || null,
			meta: {
				basket_id: basketId || null,
				quote_id: selectedQuote.id,
			},
		})

		setSavedSelection({
			...selectionSummary,
			saved_at: {
				seconds: moment().unix(),
			},
		})
		setSavingSelection(false)

		pushAlert({
			type: "SUCCESS",
			title: "Selection saved",
			body: "The chosen transfer quote has been saved on this booking for later review.",
		})
	}

	return (
		<div className={styles.lookup}>
			{savedSelection && (
				<div className={styles.hero}>
					<div className={styles.savedBadge}>
						<span>Saved selection</span>
						<strong>{savedSelection.short_description || savedSelection.transfer_type || "Transfer quote selected"}</strong>
						{formatSavedDate(savedSelection.saved_at) && <small>{formatSavedDate(savedSelection.saved_at)}</small>}
					</div>
				</div>
			)}

			<div className={styles.grid}>
				<section className={styles.panel}>
					<div className={styles.step}>
						<div className={styles.stepLabel}>
							<span className={styles.stepNumber}>1</span>
							<div>
								<strong>Route</strong>
								<small>Pick from the active golf routes we've got pricing for.</small>
							</div>
						</div>

						<div className={styles.routeRow}>
							<Select
								label="From"
								placeholder="Pickup point:"
								showLabel={true}
								activeOnHover={true}
								selected={search.from_destination || null}
								options={ROUTE_SOURCES}
								onSelect={(option) => {
									setSearch((currentSearch) => ({
										...currentSearch,
										from_destination: option.option,
										to_destination:
											currentSearch.to_destination && ROUTE_PAIRS[option.option]?.includes(currentSearch.to_destination)
												? currentSearch.to_destination
												: "",
									}))
								}}
							/>
							<div className={styles.routeArrow} aria-hidden="true">
								<ArrowRightIcon />
							</div>
							<Select
								label="To"
								placeholder={search.from_destination ? "Drop-off point:" : "Pick a pickup point first"}
								showLabel={true}
								activeOnHover={true}
								selected={search.to_destination || null}
								options={destinationOptions}
								onSelect={(option) => updateField("to_destination", option.option)}
							/>
						</div>
						{search.from_destination && (
							<small className={styles.stepMeta}>
								{Object.keys(destinationOptions).length} matching drop-off points for this pickup.
							</small>
						)}
					</div>

					<div className={styles.step}>
						<div className={styles.stepLabel}>
							<span className={styles.stepNumber}>2</span>
							<div>
								<strong>When</strong>
								<small>Choose the travel date and which time we should work to.</small>
							</div>
						</div>

						<Input
							label="Travel date"
							type="date"
							showLabel={true}
							value={search.travel_date}
							onChange={(value) => updateField("travel_date", value)}
						/>

						<div className={styles.modeSwitcher}>
							{JOURNEY_MODES.map((mode) => (
								<button
									key={mode.id}
									type="button"
									className={[styles.modeItem, journeyMode === mode.id ? styles.isActive : ""].join(" ")}
									onClick={() => changeJourneyMode(mode.id)}>
									{mode.label}
								</button>
							))}
						</div>

						<Input
							label={activeJourney.label + " time"}
							type="time"
							showLabel={true}
							value={search[journeyMode]}
							onChange={(value) => updateField(journeyMode, value)}
							note={activeJourney.hint}
						/>
					</div>

					<div className={styles.step}>
						<div className={styles.stepLabel}>
							<span className={styles.stepNumber}>3</span>
							<div>
								<strong>Passengers</strong>
							</div>
						</div>

						<div className={styles.passengersRow}>
							<Input
								label="Adults"
								type="number"
								showLabel={true}
								value={search.adults}
								onChange={(value) => updateField("adults", value)}
							/>
							<div className={styles.toggle}>
								<Checkbox
									label="Include hold luggage/golf clubs"
									checked={search.hold_luggage}
									onClick={() => updateField("hold_luggage", !search.hold_luggage)}
								/>
							</div>
						</div>
					</div>

					<div className={styles.actions}>
						<Button
							label="Search Transfers"
							icon={<SearchIcon />}
							loading={loading}
							loadingText="Searching..."
							onClick={handleSearch}
						/>
						{basketId && <small>Basket: {basketId}</small>}
					</div>

					{searchError && <div className={styles.errorBox}>{searchError}</div>}
				</section>

				<section className={styles.panel}>
					<div className={styles.sectionHeading}>
						<h4>Results</h4>
						<p>{quotes.length > 0 ? `${quotes.length} options returned from Lifts To.` : "Run a search to see live transfer options here."}</p>
					</div>

					<div className={styles.results}>
						{quotes.length === 0 && !loading && (
							<div className={styles.emptyState}>
								<h5>No quotes yet</h5>
								<p>Search with a valid route, date, and journey time to inspect live transfer options.</p>
							</div>
						)}

						{quotes.map((quote) => (
							<button
								key={quote.id}
								type="button"
								className={[styles.resultCard, selectedQuoteId === quote.id ? styles.active : ""].join(" ")}
								onClick={() => setSelectedQuoteId(quote.id)}>
								<div className={styles.resultTop}>
									<strong>{quote.shortDescription || quote.transferType || "Transfer option"}</strong>
									{quote.price !== null && <span>{quote.currency ? `${quote.currency} ` : ""}{quote.price}</span>}
								</div>
								<div className={styles.metaRow}>
									<span>{quote.journeyStatus || "Status unavailable"}</span>
									{quote.vehicleType && <span>{quote.vehicleType}</span>}
									{quote.supplierName && <span>{quote.supplierName}</span>}
								</div>
								{quote.pickupTime && <small>Pickup time: {quote.pickupTime}</small>}
							</button>
						))}
					</div>
				</section>
			</div>

			<section className={styles.detailPanel}>
				<div className={styles.detailHeader}>
					<div>
						<h4>Selected Quote</h4>
						<p>Inspect the normalized quote details below, then save the chosen option back to the booking for follow-up.</p>
					</div>

					<Button
						label="Save Selected Quote"
						small={true}
						loading={savingSelection}
						loadingText="Saving..."
						disabled={!selectedQuote}
						onClick={persistSelection}
					/>
				</div>

				{selectedQuote ? (
					<div className={styles.detailGrid}>
						<div className={styles.detailCard}>
							<h5>Summary</h5>
							<dl>
								<div>
									<dt>Status</dt>
									<dd>{selectedQuote.journeyStatus || "Unknown"}</dd>
								</div>
								<div>
									<dt>Transfer type</dt>
									<dd>{selectedQuote.transferType || "Unknown"}</dd>
								</div>
								<div>
									<dt>Vehicle</dt>
									<dd>{selectedQuote.vehicleType || "Unknown"}</dd>
								</div>
								<div>
									<dt>Supplier</dt>
									<dd>{selectedQuote.supplierName || "Unknown"}</dd>
								</div>
								<div>
									<dt>Pickup</dt>
									<dd>{selectedQuote.pickupLabel || "Unknown"}</dd>
								</div>
								<div>
									<dt>Drop-off</dt>
									<dd>{selectedQuote.dropoffLabel || "Unknown"}</dd>
								</div>
								<div>
									<dt>Price</dt>
									<dd>{selectedQuote.price !== null ? `${selectedQuote.currency ? `${selectedQuote.currency} ` : ""}${selectedQuote.price}` : "Unknown"}</dd>
								</div>
								<div>
									<dt>Capacity</dt>
									<dd>{selectedQuote.passengerCapacity || "Unknown"}</dd>
								</div>
							</dl>
						</div>

						<div className={styles.detailCard}>
							<h5>Description</h5>
							<p>{selectedQuote.shortDescription || "No description returned for this quote."}</p>

							<details className={styles.rawPayload}>
								<summary>Provider payload</summary>
								<pre>{JSON.stringify(selectedQuote.raw, null, 2)}</pre>
							</details>
						</div>
					</div>
				) : (
					<div className={styles.emptyState}>
						<h5>No quote selected</h5>
						<p>Choose a result from the lookup list to inspect its details.</p>
					</div>
				)}
			</section>
		</div>
	)
}
